import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Star } from "lucide-react";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";

/**
 * Minimal-Shell für Google-Ads-Landing-Pages:
 * kein Navigationsmenü (keine Ablenkung), Logo, Telefon, ein CTA.
 * Setzt noindex (Ads-LPs sollen nicht mit der Hauptseite um Rankings konkurrieren).
 */
export function useLpMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    let desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    if (desc && description) desc.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      robots.remove();
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

export function GoogleStarsBadge() {
  return (
    <span data-testid="lp-google-badge" className="inline-flex items-center gap-2 rounded-full bg-white border border-cream-300 px-4 py-2 shadow-soft">
      <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
      </svg>
      <span className="font-semibold text-sand-800 text-sm">4,8</span>
      <span className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, idx) => (
          <Star key={idx} className="h-3 w-3 fill-[#FBBC04] text-[#FBBC04]" strokeWidth={1} />
        ))}
      </span>
      <span className="text-[11px] text-sand-500">183 Rezensionen bei Google</span>
    </span>
  );
}

export default function LpShell({ children, ctaLabel = "Kostenlose Beratung", ctaHref = "#lp-kontakt" }) {
  return (
    <div className="min-h-screen bg-cream-50">
      <header className="sticky top-0 z-40 bg-cream-50/95 backdrop-blur border-b border-cream-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-semibold text-sand-800 tracking-tight" data-testid="lp-logo">
            Dickies-<span className="text-terra">Campus</span>
          </Link>
          <div className="flex items-center gap-4">
            <TrackedPhoneLink source="lp-header" data-testid="lp-phone" className="hidden sm:inline-flex items-center gap-2 text-[13.5px] font-medium text-sand-700 hover:text-sand-900">
              <Phone className="h-4 w-4 text-terra" strokeWidth={1.8} /> 0211 29273330
            </TrackedPhoneLink>
            <a href={ctaHref} data-testid="lp-header-cta"
              className="inline-flex items-center rounded-full bg-terra hover:bg-terra-dark text-white px-5 h-10 text-[13px] font-semibold transition-colors">
              {ctaLabel}
            </a>
          </div>
        </div>
      </header>

      {children}

      <footer className="border-t border-cream-300 bg-cream-100 py-8 mt-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-[12.5px] text-sand-600">
          <span>© {new Date().getFullYear()} Dickies-Campus GmbH · Düsseldorf</span>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-sand-800">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-sand-800">Datenschutz</Link>
            <Link to="/agb" className="hover:text-sand-800">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
